"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.RPCHandler = exports.ConnectionStatsEvent = exports.ConnectionStatusEvent = exports.RateLimitUpdatedEvent = void 0;
exports.rpcJSONReplacer = rpcJSONReplacer;
exports.rpcJSONReceiver = rpcJSONReceiver;
exports.fromStatus = fromStatus;
const platform_1 = __importStar(require("@hcengineering/platform"));
const msgpackr_1 = require("msgpackr");
function isTotalArray(value) {
    return Array.isArray(value) && (value.total !== undefined || value.lookupMap !== undefined);
}
function rpcJSONReplacer(key, value) {
    if (isTotalArray(value)) {
        return {
            dataType: 'TotalArray',
            total: value.total,
            lookupMap: value.lookupMap,
            value: [...value]
        };
    }
    else if (typeof value === 'object' &&
        value !== null &&
        'domain' in value &&
        typeof value.domain === 'string' &&
        'value' in value &&
        isTotalArray(value.value)) {
        return {
            ...value,
            value: {
                dataType: 'TotalArray',
                total: value.value.total,
                lookupMap: value.value.lookupMap,
                value: [...value.value]
            }
        };
    }
    else {
        return value ?? null;
    }
}
function rpcJSONReceiver(key, value) {
    if (typeof value === 'object' && value !== null) {
        // Array check: a document holding the same dataType must not be boxed by Object.assign.
        if (value.dataType === 'TotalArray' && Array.isArray(value.value)) {
            return Object.assign(value.value, { total: value.total, lookupMap: value.lookupMap });
        }
        else if ('domain' in value &&
            typeof value.domain === 'string' &&
            'value' in value &&
            value.value?.dataType === 'TotalArray' &&
            Array.isArray(value.value.value)) {
            return {
                ...value,
                value: Object.assign(value.value.value, { total: value.value.total, lookupMap: value.value.lookupMap })
            };
        }
    }
    return value;
}
// Platform-event names the connection broadcasts so the UI can show network state.
exports.RateLimitUpdatedEvent = 'rate-limit-updated'; // data: RateLimitInfo | undefined
exports.ConnectionStatusEvent = 'connection-status'; // data: boolean (connected)
exports.ConnectionStatsEvent = 'connection-stats'; // data: { sent, received, sentBytes, receivedBytes, latency }
class RPCHandler {
    packr = new msgpackr_1.Packr({ structuredClone: true, bundleStrings: true, copyBuffers: false });
    protoSerialize(object, binary) {
        if (!binary) {
            return JSON.stringify(object, rpcJSONReplacer);
        }
        return new Uint8Array(this.packr.pack(object));
    }
    protoDeserialize(data, binary) {
        if (!binary) {
            let _data = data;
            if (_data instanceof ArrayBuffer) {
                const decoder = new TextDecoder();
                _data = decoder.decode(_data);
            }
            try {
                return JSON.parse(_data.toString(), rpcJSONReceiver);
            }
            catch (err) {
                if ((err.message ?? '').includes('Unexpected token')) {
                    return this.packr.unpack(new Uint8Array(data));
                }
            }
        }
        return this.packr.unpack(new Uint8Array(data));
    }
    /**
     * @public
     * @param object -
     * @returns
     */
    serialize(object, binary) {
        if (object.result !== undefined) {
            ;
            object.result = rpcJSONReplacer('result', object.result);
        }
        return this.protoSerialize(object, binary);
    }
    /**
     * @public
     * @param response -
     * @returns
     */
    readResponse(response, binary) {
        const data = this.protoDeserialize(response, binary);
        if (data.result !== undefined) {
            data.result = rpcJSONReceiver('result', data.result);
        }
        return data;
    }
    /**
     * @public
     * @param request -
     * @returns
     */
    readRequest(request, binary) {
        const result = this.protoDeserialize(request, binary);
        if (typeof result.method !== 'string') {
            throw new platform_1.PlatformError(new platform_1.Status(platform_1.Severity.ERROR, platform_1.default.status.BadRequest, {}));
        }
        return result;
    }
}
exports.RPCHandler = RPCHandler;
/**
 * @public
 * @param status -
 * @param id -
 * @returns
 */
function fromStatus(status, id) {
    return { id, error: status };
}
//# sourceMappingURL=rpc.js.map